import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { libraryQuestionPath } from '@/constants/appRoutes';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';
import { libraryQuestionsApi, type LibraryAnswerFeedItem } from '@/services/api';
import { useChatShell } from '../ChatShellContext';
import { AnswerVote, byAnswerRank, CommunityPostHeading, RightAnswerControl } from './CommunityPost';

export function CommunityAskFeed() {
  const { communityVersion } = useChatShell();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<LibraryAnswerFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votingId, setVotingId] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    libraryQuestionsApi
      .listAnswers({ limit: 50 })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load community answers.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [communityVersion]);

  const vote = (answer: LibraryAnswerFeedItem, direction: 1 | -1) => {
    if (votingId) return;
    setVotingId(answer.id);
    void libraryQuestionsApi
      .voteAnswer(answer.question_id, answer.id, direction)
      .then((updated) => {
        setItems((current) =>
          byAnswerRank(
            current.map((item) =>
              item.id === updated.id ? { ...item, score: updated.score, my_vote: updated.my_vote } : item,
            ),
          ),
        );
      })
      .finally(() => setVotingId(null));
  };

  const mark = (answer: LibraryAnswerFeedItem) => {
    if (markingId) return;
    setMarkingId(answer.id);
    void libraryQuestionsApi
      .acceptAnswer(answer.question_id, answer.id)
      .then((updated) => {
        const flags = new Map(updated.answers.map((item) => [item.id, item.is_accepted]));
        setItems((current) =>
          byAnswerRank(
            current.map((item) => {
              if (item.question_id !== updated.id) return item;
              const accepted = flags.get(item.id);
              return accepted == null ? item : { ...item, is_accepted: accepted };
            }),
          ),
        );
      })
      .finally(() => setMarkingId(null));
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (error) {
    return <p className="py-6 text-center text-sm text-destructive">{error}</p>;
  }

  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No community answers yet.</p>;
  }

  return (
    <ul className="mt-2 space-y-3">
      {items.map((answer) => (
        <li key={answer.id} className="rounded-xl border border-border bg-card px-3 py-3">
          <Link
            to={libraryQuestionPath(answer.question_id)}
            className="mb-2 block truncate text-xs text-muted-foreground hover:underline"
          >
            {answer.question_body}
          </Link>
          <div className="flex gap-2">
            <AnswerVote
              score={answer.score}
              myVote={answer.my_vote}
              disabled={votingId === answer.id}
              onVote={(direction) => vote(answer, direction)}
            />
            <div className="min-w-0 flex-1">
              <CommunityPostHeading
                authorName={answer.author_name}
                createdAt={answer.created_at}
                meta={
                  <RightAnswerControl
                    accepted={answer.is_accepted}
                    canMark={isAdmin}
                    disabled={markingId === answer.id}
                    onToggle={() => mark(answer)}
                  />
                }
              />
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{answer.body}</p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
